export const WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE = 'workspace-context-upload' as const;

export type WorkspaceContextUploadStatus = 'queued' | 'processing' | 'ready' | 'failed';
export type ChatStatusItemType =
    | typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE;

export type ChatStatusItemBase<TType extends ChatStatusItemType, TStatus extends string> = {
    id: string;
    type: TType;
    status: TStatus;
    updatedAt: number;
};

export type ChatStatusItemEventBase<TType extends ChatStatusItemType> = {
    id: string;
    type: TType;
};

export type WorkspaceContextUploadStatusItem = ChatStatusItemBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
    WorkspaceContextUploadStatus
> & {
    contextFileId: string;
    filename: string;
    title: string;
    userVisibleError: string;
    userVisibleNotice: string;
};

export type WorkspaceContextUploadStatusItemEvent = (
    | WorkspaceContextUploadStatusItemUpsertEvent
    | WorkspaceContextUploadStatusItemRemoveEvent
);

type WorkspaceContextUploadStatusItemUpsertEvent = ChatStatusItemEventBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
> & {
    contextFileId?: string;
    filename?: string;
    title?: string;
    status: WorkspaceContextUploadStatus;
    userVisibleError?: string;
    userVisibleNotice?: string;
    removed?: false;
};

type WorkspaceContextUploadStatusItemRemoveEvent = ChatStatusItemEventBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
> & {
    contextFileId?: string;
    removed: true;
};

export type ChatStatusItem =
    | WorkspaceContextUploadStatusItem;

export type ChatStatusItemEvent =
    | WorkspaceContextUploadStatusItemEvent;

export function chatStatusItemEventFromUnknown(value: unknown): ChatStatusItemEvent | null {
    if (!isRecord(value)) {
        return null;
    }

    switch (value.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return workspaceContextUploadStatusItemEventFromRecord(value);
        default:
            return null;
    }
}

export function chatStatusItemFromEvent(event: ChatStatusItemEvent, now: number): ChatStatusItem | null {
    if (chatStatusItemEventRemovesItem(event)) {
        return null;
    }

    switch (event.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return workspaceContextUploadStatusItemFromEvent(event, now);
        default:
            return null;
    }
}

export function chatStatusItemIdFromEvent(event: ChatStatusItemEvent): string {
    switch (event.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return stringValue(event.id) || stringValue(event.contextFileId);
        default:
            return '';
    }
}

export function chatStatusItemEventRemovesItem(
    event: ChatStatusItemEvent,
): event is Extract<ChatStatusItemEvent, {removed: true}> {
    return event.removed === true;
}

export function chatStatusItemsEqual(left: ChatStatusItem, right: ChatStatusItem): boolean {
    return left.id === right.id
        && left.type === right.type
        && left.status === right.status
        && statusItemSpecificFingerprint(left) === statusItemSpecificFingerprint(right);
}

export function isWorkspaceContextUploadStatusItem(
    item: ChatStatusItem,
): item is WorkspaceContextUploadStatusItem {
    return item.type === WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE;
}

export function workspaceContextUploadStatus(value: unknown): WorkspaceContextUploadStatus | null {
    return value === 'queued' || value === 'processing' || value === 'ready' || value === 'failed'
        ? value
        : null;
}

function workspaceContextUploadStatusItemEventFromRecord(
    record: Record<string, unknown>,
): WorkspaceContextUploadStatusItemEvent | null {
    if (record.removed === true) {
        return {
            type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
            id: stringValue(record.id),
            contextFileId: stringValue(record.contextFileId),
            removed: true,
        };
    }

    const status = workspaceContextUploadStatus(record.status);
    if (status === null) {
        return null;
    }

    return {
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        id: stringValue(record.id),
        contextFileId: stringValue(record.contextFileId),
        filename: stringValue(record.filename),
        title: stringValue(record.title),
        status,
        userVisibleError: stringValue(record.userVisibleError),
        userVisibleNotice: stringValue(record.userVisibleNotice),
    };
}

function workspaceContextUploadStatusItemFromEvent(
    event: WorkspaceContextUploadStatusItemUpsertEvent,
    now: number,
): WorkspaceContextUploadStatusItem | null {
    const id = chatStatusItemIdFromEvent(event);
    const status = workspaceContextUploadStatus(event.status);
    if (id === '' || status === null) {
        return null;
    }

    return {
        id,
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        contextFileId: stringValue(event.contextFileId) || id,
        status,
        filename: stringValue(event.filename),
        title: stringValue(event.title),
        userVisibleError: stringValue(event.userVisibleError),
        userVisibleNotice: stringValue(event.userVisibleNotice),
        updatedAt: now,
    };
}

function statusItemSpecificFingerprint(item: ChatStatusItem): string {
    switch (item.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return [
                item.contextFileId,
                item.filename,
                item.title,
                item.userVisibleError,
                item.userVisibleNotice,
            ].join('\u0001');
        default:
            return '';
    }
}

function stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
